/**
 * Main App Component — JML Automator.
 *
 * Routes are code-split via React.lazy so the initial bundle stays small.
 * Public auth pages eagerly load (they're tiny and on the critical path).
 * All protected routes are lazy-loaded behind a Suspense boundary.
 */
import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './components/auth/ProtectedRoute';

// ─── Eagerly loaded (small, critical path) ────────────────────────────────
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import RegisterPending from './pages/RegisterPending';
import PendingApproval from './pages/PendingApproval';
import AcceptInvitation from './pages/AcceptInvitation';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// ─── Code-split protected pages ───────────────────────────────────────────
const Dashboard = lazy(() => import('./pages/Dashboard'));
const WorkflowRun = lazy(() => import('./pages/WorkflowRun'));
const Joiner = lazy(() => import('./pages/Joiner'));
const Mover = lazy(() => import('./pages/Mover'));
const Leaver = lazy(() => import('./pages/Leaver'));
const Audit = lazy(() => import('./pages/Audit'));
const Settings = lazy(() => import('./pages/Settings'));
const Bundles = lazy(() => import('./pages/Bundles'));
const Groups = lazy(() => import('./pages/Groups'));
const BulkCreateGroups = lazy(() => import('./pages/BulkCreateGroups'));
const BulkCalendarSharing = lazy(() => import('./pages/BulkCalendarSharing'));
const BulkSetManager = lazy(() => import('./pages/BulkSetManager'));
const WorkflowEditor = lazy(() => import('./pages/WorkflowEditor'));
const RoleManagement = lazy(() => import('./pages/RoleManagement'));
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdminDashboard'));
const PlanManagement = lazy(() => import('./pages/PlanManagement'));
const M365Users = lazy(() => import('./pages/M365Users'));
const HRIntegration = lazy(() => import('./pages/HRIntegration'));
const TeamManagement = lazy(() => import('./pages/TeamManagement'));
const AzureADSettings = lazy(() => import('./pages/AzureADSettings'));
const JMLAutomation = lazy(() => import('./pages/JMLAutomation'));
const People = lazy(() => import('./pages/People'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Templates = lazy(() => import('./pages/Templates'));
const TemplateEditor = lazy(() => import('./pages/TemplateEditor'));
const LicenseOptimization = lazy(() => import('./pages/LicenseOptimization'));
const LicenseOptimizationDetail = lazy(() => import('./pages/LicenseOptimizationDetail'));
const Graph = lazy(() => import('./pages/Graph'));

import './App.css';

const RouteFallback = (): React.ReactElement => (
  <div className="flex h-screen items-center justify-center bg-slate-50">
    <div className="flex flex-col items-center gap-3 text-slate-500">
      <div
        className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600"
        aria-label="Loading"
      />
      <span className="text-sm">Loading…</span>
    </div>
  </div>
);

const App = (): React.ReactElement => {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/app/login" element={<Login />} />
        <Route path="/app/register" element={<Register />} />
        <Route path="/app/register-pending" element={<RegisterPending />} />
        <Route path="/app/pending-approval" element={<PendingApproval />} />
        <Route path="/app/accept-invitation" element={<AcceptInvitation />} />
        <Route path="/app/forgot-password" element={<ForgotPassword />} />
        <Route path="/app/reset-password" element={<ResetPassword />} />

        {/* Protected Routes */}
        <Route
          path="/app/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/workflows/:runId"
          element={
            <ProtectedRoute>
              <WorkflowRun />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/joiner"
          element={
            <ProtectedRoute>
              <Joiner />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/mover"
          element={
            <ProtectedRoute>
              <Mover />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/leaver"
          element={
            <ProtectedRoute>
              <Leaver />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/audit"
          element={
            <ProtectedRoute>
              <Audit />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/bundles"
          element={
            <ProtectedRoute>
              <Bundles />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/groups"
          element={
            <ProtectedRoute>
              <Groups />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/groups/bulk-create"
          element={
            <ProtectedRoute>
              <BulkCreateGroups />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/people/bulk-calendar"
          element={
            <ProtectedRoute>
              <BulkCalendarSharing />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/people/bulk-set-manager"
          element={
            <ProtectedRoute>
              <BulkSetManager />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/templates"
          element={
            <ProtectedRoute>
              <Templates />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/templates/new"
          element={
            <ProtectedRoute>
              <TemplateEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/templates/:templateId"
          element={
            <ProtectedRoute>
              <TemplateEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/license-optimization"
          element={
            <ProtectedRoute>
              <LicenseOptimization />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/license-optimization/:reportId"
          element={
            <ProtectedRoute>
              <LicenseOptimizationDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/graph"
          element={
            <ProtectedRoute>
              <Graph />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/workflow-editor"
          element={
            <ProtectedRoute>
              <WorkflowEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/roles"
          element={
            <ProtectedRoute>
              <RoleManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/admin"
          element={
            <ProtectedRoute>
              <SuperAdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/admin/plans"
          element={
            <ProtectedRoute>
              <PlanManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/m365-users"
          element={
            <ProtectedRoute>
              <M365Users />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/hr-integration"
          element={
            <ProtectedRoute>
              <HRIntegration />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/team"
          element={
            <ProtectedRoute>
              <TeamManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/azure-ad-settings"
          element={
            <ProtectedRoute>
              <AzureADSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/jml-automation"
          element={
            <ProtectedRoute>
              <JMLAutomation />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/people"
          element={
            <ProtectedRoute>
              <People />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/onboarding"
          element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

export default App;
